import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Code2,
  ExternalLink,
  Eye,
  History,
  Info,
  LayoutDashboard,
  LayoutTemplate,
  Link2,
  PencilRuler,
  RotateCcw,
  Rocket,
  Save,
  Share2,
  Tv,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { adminApi, type DraftCheck } from "../../api/admin";
import { ApiError } from "../../api/client";
import { ownerApi } from "../../api/owner";
import { RequestTvPublishDialog } from "../../components/RequestTvPublishDialog";
import { ShareDialog } from "../../components/ShareDialog";
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  Select,
  SkeletonRows,
  Tabs,
  Textarea,
  formatDateTime,
  formatRelative,
  useConfirm,
  useToast,
} from "../../ds";
import { usePageTitle } from "../../shell/AppShell";
import { PagePicker } from "../../shell/PagePicker";
import { TvFrame, type TvFrameHandle, type TvFramePageInfo } from "../../shell/TvFrame";
import type { DraftSnapshot, ReportVersion } from "../../types";
import { reportStatus } from "./reportStatus";
import { ScreensAccessTab } from "./ScreensAccessTab";
import { ShareLinksTab } from "./ShareLinksTab";
import { CATEGORIES_KEY, CategoryTag, sortCategories } from "./reportCategories";
import { ReportDesignCard } from "../../studio/DesignScore"; // workstream D
import { SaveAsTemplateDialog } from "./SaveAsTemplateDialog"; // workstream D

type Tab = "overview" | "access" | "share" | "history" | "advanced";

export function ReportWorkspacePage() {
  const { reportId = "" } = useParams();
  const loc = useLocation();
  const isOwner = loc.pathname.startsWith("/owner");
  const api = isOwner ? ownerApi : adminApi;
  const base = isOwner ? "/owner" : "/admin";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");
  const [publishOpen, setPublishOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [tvOpen, setTvOpen] = useState(false);
  const [pages, setPages] = useState<TvFramePageInfo[]>([]);
  const [pageId, setPageId] = useState<string | null>(null);
  const frame = useRef<TvFrameHandle>(null);

  const report = useQuery({ queryKey: ["report", base, reportId], queryFn: () => api.getReport(reportId) });
  const draft = useQuery({ queryKey: ["draft", base, reportId], queryFn: () => api.getDraft(reportId) });
  const versions = useQuery({ queryKey: ["versions", base, reportId], queryFn: () => api.listVersions(reportId) });
  usePageTitle(report.data?.name);

  const st = report.data ? reportStatus(report.data) : null;
  const latest = (versions.data ?? []).slice().sort((a, b) => b.version_number - a.version_number)[0];
  const draftNewer = draft.data && latest ? new Date(draft.data.updated_at).getTime() > new Date(latest.created_at).getTime() + 2000 : !!draft.data && !latest;

  if (report.isError) {
    return (
      <div className="ds-page">
        <Card>
          <EmptyState icon={AlertTriangle} title="This report couldn't be opened" description={report.error instanceof Error ? report.error.message : undefined} action={<Button onClick={() => navigate(`${base}/reports`)}>Back to reports</Button>} />
        </Card>
      </div>
    );
  }

  return (
    <div className="ds-page ds-page--wide" style={{ maxWidth: 1680 }}>
      <PageHeader
        eyebrow={[report.data?.category_name, report.data?.owner_name ? `Owner · ${report.data.owner_name}` : null].filter(Boolean).join(" · ") || "Report"}
        title={
          <span className="ds-row" style={{ gap: 12 }}>
            {report.data?.name ?? "…"}
            {st ? <Badge tone={st.tone} dot title={st.hint}>{st.label}</Badge> : null}
          </span>
        }
        subtitle={report.data?.description || undefined}
        actions={
          <>
            <Button icon={ExternalLink} onClick={() => window.open(`/tv-preview/preview.html?reportId=${reportId}${isOwner ? "&mode=owner" : ""}`, "_blank", "noopener")}>
              Preview as TV
            </Button>
            <Button icon={LayoutTemplate} onClick={() => setTemplateOpen(true)} disabled={!draft.data}>Save as template</Button>
            <Button icon={PencilRuler} onClick={() => navigate(`${base}/reports/${reportId}/edit`)}>Edit in designer</Button>
            <Button icon={Share2} onClick={() => setShareOpen(true)}>Share</Button>
            {isOwner ? <Button icon={Tv} onClick={() => setTvOpen(true)} title="Ask IT to put this report on the TV screens">Request TV publish</Button> : null}
            <Button variant="primary" icon={Rocket} onClick={() => setPublishOpen(true)}>Publish…</Button>
          </>
        }
      />

      {draft.data ? (
        <div style={{ marginBottom: 16 }}>
          {!latest ? (
            <Banner tone="info" title="This report has never been published">
              You're looking at the draft (revision {draft.data.revision_number}, edited {formatRelative(draft.data.updated_at)}). TVs and viewers see nothing until you publish.
            </Banner>
          ) : draftNewer ? (
            <Banner tone="warning" title="The draft has changes that aren't live yet">
              Draft revision {draft.data.revision_number} was edited {formatRelative(draft.data.updated_at)}. Screens still show version {latest.version_number}, published {formatRelative(latest.created_at)}.
            </Banner>
          ) : (
            <Banner tone="success" title={`Live: version ${latest.version_number}`}>
              Published {formatRelative(latest.created_at)}. The draft matches what's on screens.
            </Banner>
          )}
        </div>
      ) : null}

      <div style={{ marginBottom: 16 }}>
        <Tabs<Tab>
          value={tab}
          onChange={setTab}
          tabs={[
            { value: "overview", label: "Overview", icon: LayoutDashboard },
            ...(isOwner ? [] : [{ value: "access" as Tab, label: "Screens & access", icon: Tv }]),
            { value: "share", label: "Share links", icon: Link2 },
            { value: "history", label: "Version history", icon: History, count: versions.data?.length },
            ...(isOwner ? [] : [{ value: "advanced" as Tab, label: "Advanced", icon: Code2 }]),
          ]}
        />
      </div>

      {tab === "overview" ? (
        <div className="ds-grid" style={{ gridTemplateColumns: "minmax(0, 1fr) 300px", alignItems: "start" }}>
          <div className="ds-stack" style={{ gap: 12 }}>
            <div style={{ marginBottom: 12 }}><PagePicker pages={pages} value={pageId} onChange={(id) => { setPageId(id); frame.current?.post({ type: "mrp:selectPage", pageId: id }); }} /></div>
            <TvFrame
              ref={frame}
              reportId={reportId}
              source={isOwner ? "owner" : "admin"}
              onLoaded={(ps, id) => { setPages(ps); setPageId(id); }}
              onPage={setPageId}
              reloadKey={draft.data?.revision_number}
            />
            <div className="ds-small ds-faint">Draft preview with live data — rendered by the same engine the office TVs use.</div>
          </div>
          <div className="ds-stack">
            <Card title="At a glance">
              <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 14px", fontSize: 13.5 }}>
                {isOwner && report.data?.category_name ? (
                  <>
                    <dt className="ds-muted">Category</dt>
                    <dd style={{ margin: 0 }}><CategoryTag name={report.data.category_name} /></dd>
                  </>
                ) : null}
                <dt className="ds-muted">Pages</dt><dd style={{ margin: 0 }}>{pages.length || report.data?.page_count || "—"}</dd>
                <dt className="ds-muted">Draft</dt><dd style={{ margin: 0 }}>rev {draft.data?.revision_number ?? "—"} · {formatRelative(draft.data?.updated_at)}</dd>
                <dt className="ds-muted">Live</dt><dd style={{ margin: 0 }}>{latest ? `v${latest.version_number} · ${formatRelative(latest.created_at)}` : "Not published"}</dd>
                <dt className="ds-muted">Screens</dt>
                <dd style={{ margin: 0 }}>
                  {report.data?.screen_count ? (
                    <span title={(report.data.screen_names ?? []).join(", ")}>{screenBreakdown(report.data.screen_count, report.data.laptop_count ?? 0)}</span>
                  ) : (report.data?.device_group_names ?? []).length ? (
                    <span className="ds-row" style={{ flexWrap: "wrap", gap: 4 }}>
                      {(report.data?.device_group_names ?? []).map((g) => <Badge key={g} tone={g === "Laptops" ? "neutral" : "success"}>{g}</Badge>)}
                    </span>
                  ) : "None yet"}
                </dd>
              </dl>
              {!isOwner && report.data ? <CategoryPicker reportId={reportId} categoryId={report.data.category_id ?? null} /> : null}
            </Card>
            <ReportDesignCard area={isOwner ? "owner" : "admin"} reportId={reportId} revision={draft.data?.revision_number} onSelectWidget={(pid) => { setPageId(pid); frame.current?.post({ type: "mrp:selectPage", pageId: pid }); }} onOpenDesigner={() => navigate(`${base}/reports/${reportId}/edit`)} />
            <Card title="Next steps">
              <div className="ds-stack" style={{ gap: 8 }}>
                <Button icon={PencilRuler} onClick={() => navigate(`${base}/reports/${reportId}/edit`)} style={{ justifyContent: "flex-start" }}>Change the layout or data</Button>
                <Button icon={CheckCircle2} onClick={() => setPublishOpen(true)} style={{ justifyContent: "flex-start" }}>Check & publish</Button>
                {!isOwner ? <Button icon={Tv} onClick={() => setTab("access")} style={{ justifyContent: "flex-start" }}>Choose which screens show it</Button> : null}
              </div>
            </Card>
          </div>
        </div>
      ) : null}

      {tab === "access" && !isOwner ? <ScreensAccessTab reportId={reportId} report={report.data} /> : null}
      {tab === "share" ? <ShareLinksTab reportId={reportId} base={isOwner ? "/owner" : "/admin"} published={!!latest} /> : null}
      {tab === "history" ? <HistoryTab reportId={reportId} isOwner={isOwner} base={base} versions={versions.data} loading={versions.isLoading} /> : null}
      {tab === "advanced" && !isOwner ? <AdvancedTab reportId={reportId} /> : null}

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} reportId={reportId} reportName={report.data?.name} />
      {isOwner ? <RequestTvPublishDialog open={tvOpen} onClose={() => setTvOpen(false)} reportId={reportId} reportName={report.data?.name} /> : null}
      <SaveAsTemplateDialog open={templateOpen} onClose={() => setTemplateOpen(false)} reportId={reportId} reportName={report.data?.name ?? "Report"} definition={draft.data?.definition_json} />
      <PublishDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        reportId={reportId}
        isOwner={isOwner}
        onPublished={() => {
          qc.invalidateQueries({ queryKey: ["versions", base, reportId] });
          qc.invalidateQueries({ queryKey: ["report", base, reportId] });
          qc.invalidateQueries({ queryKey: ["reports"] });
        }}
      />
    </div>
  );
}

/** Admin: which company group (MOP, MOS, ...) the report sits under on the
 * TV Reports menu. Saves as soon as it changes. */
function CategoryPicker({ reportId, categoryId }: { reportId: string; categoryId: string | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const cats = useQuery({ queryKey: CATEGORIES_KEY, queryFn: adminApi.listReportCategories, staleTime: 30_000, retry: false });
  const list = sortCategories(cats.data ?? []);
  const [value, setValue] = useState(categoryId ?? "");
  useEffect(() => setValue(categoryId ?? ""), [categoryId]);
  const save = useMutation({
    mutationFn: (next: string) => adminApi.updateReport(reportId, { category_id: next || null }),
    onSuccess: (_r, next) => {
      const name = list.find((c) => c.id === next)?.name;
      if (name) toast.success(`Moved to ${name}`, `The TV Reports menu shows it under ${name}.`);
      else toast.success("Category removed", "It now appears under Uncategorized.");
      qc.invalidateQueries({ queryKey: ["report"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
      qc.invalidateQueries({ queryKey: CATEGORIES_KEY });
    },
    onError: (e) => {
      setValue(categoryId ?? "");
      toast.error("Couldn't change the category", e instanceof Error ? e.message : undefined);
    },
  });
  if (cats.isError) return null; // server without categories: nothing to pick
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--divider)" }}>
      <Field label="Category" htmlFor="report-category" hint="Decides which group (MOP, MOS…) this report appears under on the TV Reports menu.">
        <Select
          id="report-category"
          inputSize="sm"
          value={value}
          disabled={cats.isLoading || save.isPending}
          onChange={(e) => {
            setValue(e.target.value);
            save.mutate(e.target.value);
          }}
        >
          <option value="">No category (Uncategorized)</option>
          {list.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          {value && !cats.isLoading && !list.some((c) => c.id === value) ? <option value={value}>Current category</option> : null}
        </Select>
      </Field>
    </div>
  );
}

export function PublishDialog({ open, onClose, reportId, isOwner, onPublished }: { open: boolean; onClose: () => void; reportId: string; isOwner: boolean; onPublished: () => void }) {
  const api = isOwner ? ownerApi : adminApi;
  const toast = useToast();
  const check = useQuery<DraftCheck>({ queryKey: ["validate", reportId, open], queryFn: () => api.validateDraft(reportId), enabled: open, staleTime: 0 });
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) setError(null);
  }, [open]);
  const publish = useMutation({
    mutationFn: () => api.publishReport(reportId),
    onSuccess: () => {
      toast.success("Published", "Screens pick up the new version within a minute.");
      onPublished();
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.validationErrors?.join("\n") ?? e.message) : "Publishing failed."),
  });
  const c = check.data;
  const changes = c?.changes;
  const changeLines = changes
    ? [
        changes.firstPublish ? "First publish of this report" : null,
        changes.pagesAdded.length ? `Pages added: ${changes.pagesAdded.join(", ")}` : null,
        changes.pagesRemoved.length ? `Pages removed: ${changes.pagesRemoved.join(", ")}` : null,
        changes.pagesRenamed?.length ? `Pages renamed: ${changes.pagesRenamed.join(", ")}` : null,
        !changes.firstPublish && changes.widgetsAdded ? `${changes.widgetsAdded} visual(s) added` : null,
        changes.widgetsRemoved ? `${changes.widgetsRemoved} visual(s) removed` : null,
        changes.widgetsChanged ? `${changes.widgetsChanged} visual(s) changed` : null,
        changes.settingsChanged?.length ? `Report settings changed: ${changes.settingsChanged.join(", ")}` : null,
      ].filter(Boolean)
    : [];
  const nothingChanged = changes && !changes.firstPublish && changeLines.length === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Publish to screens"
      description="We check the draft first. Publishing creates a new version; you can roll back any time."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={Rocket} loading={publish.isPending} disabled={!c?.valid || check.isFetching} onClick={() => publish.mutate()}>
            {nothingChanged ? "Publish anyway" : "Publish now"}
          </Button>
        </>
      }
    >
      {check.isLoading || check.isFetching ? (
        <SkeletonRows rows={4} />
      ) : check.isError ? (
        <Banner tone="danger" title="Couldn't check the draft">{check.error instanceof Error ? check.error.message : null}</Banner>
      ) : c ? (
        <div className="ds-stack">
          {c.valid ? (
            <Banner tone="success" title="Ready to publish">
              {c.summary.pages} page(s), {c.summary.widgets} visual(s) passed every check.
            </Banner>
          ) : (
            <Banner tone="danger" title={`${c.errors.length} problem(s) must be fixed first`}>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {c.errors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </Banner>
          )}
          {c.summary.unboundWidgets.length ? (
            <Banner tone="warning" title={`${c.summary.unboundWidgets.length} visual(s) have no data yet`}>
              They'll show an empty state on screens: {c.summary.unboundWidgets.slice(0, 5).map((w) => `${w.title} (${w.page})`).join(", ")}
              {c.summary.unboundWidgets.length > 5 ? "…" : ""}
            </Banner>
          ) : null}
          <Card title="What changes on screens">
            {nothingChanged ? (
              <div className="ds-muted">The draft is identical to the live version.</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
                {changeLines.map((l) => <li key={l as string}>{l}</li>)}
              </ul>
            )}
          </Card>
          {error ? <Banner tone="danger" title="Publishing failed">{error}</Banner> : null}
        </div>
      ) : null}
    </Modal>
  );
}

function HistoryTab({ reportId, isOwner, base, versions, loading }: { reportId: string; isOwner: boolean; base: string; versions?: ReportVersion[]; loading: boolean }) {
  const confirm = useConfirm();
  const toast = useToast();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: adminApi.listUsers, enabled: !isOwner });
  const nameOf = useMemo(() => new Map((users.data ?? []).map((u) => [u.id, u.display_name || u.email])), [users.data]);
  const sorted = (versions ?? []).slice().sort((a, b) => b.version_number - a.version_number);
  const rollback = useMutation({
    mutationFn: (v: ReportVersion) => adminApi.rollbackReport(reportId, { version_id: v.id, reason: `Rolled back from the platform UI to v${v.version_number}` }),
    onSuccess: (_d, v) => {
      toast.success(`Screens now show version ${v.version_number}`);
      qc.invalidateQueries({ queryKey: ["versions"] });
      qc.invalidateQueries({ queryKey: ["report"] });
    },
    onError: (e) => toast.error("Rollback failed", e instanceof Error ? e.message : undefined),
  });
  return (
    <>
    <DraftHistoryCard reportId={reportId} isOwner={isOwner} base={base} nameOf={nameOf} />
    <div style={{ height: 16 }} />
    <Card flush title="Published versions" subtitle="Every publish is kept. Roll back if a change goes wrong.">
      <div style={{ height: 12 }} />
      <DataTable<ReportVersion>
        rows={sorted}
        loading={loading}
        rowKey={(v) => v.id}
        empty={<EmptyState icon={History} title="No versions yet" description="Publish the report to create version 1." />}
        columns={[
          { key: "v", header: "Version", cell: (v) => <strong>v{v.version_number}</strong> },
          { key: "when", header: "Published", cell: (v) => <span title={formatDateTime(v.created_at)}>{formatRelative(v.created_at)}</span> },
          { key: "by", header: "By", cell: (v) => <span className="ds-muted">{nameOf.get(v.created_by) ?? "—"}</span> },
          { key: "note", header: "Note", cell: (v) => <span className="ds-muted">{v.change_summary ?? "—"}</span> },
          {
            key: "act",
            header: "",
            align: "right",
            cell: (v) =>
              v === sorted[0] ? (
                <Badge tone="success" dot>Current</Badge>
              ) : isOwner ? null : (
                <Button
                  size="sm"
                  icon={RotateCcw}
                  onClick={async () => {
                    if (await confirm({ title: `Roll back to version ${v.version_number}?`, message: "Screens switch to that version within a minute. The draft is not changed.", confirmLabel: "Roll back" })) rollback.mutate(v);
                  }}
                >
                  Roll back
                </Button>
              ),
          },
        ]}
      />
    </Card>
    </>
  );
}

const SNAPSHOT_KIND: Record<string, { label: string; tone: "neutral" | "info" | "warning" }> = {
  auto: { label: "Auto checkpoint", tone: "neutral" },
  manual: { label: "Saved by you", tone: "info" },
  before_restore: { label: "Before a restore", tone: "warning" },
};

function DraftHistoryCard({ reportId, isOwner, base, nameOf }: { reportId: string; isOwner: boolean; base: string; nameOf: Map<string, string> }) {
  const api = isOwner ? ownerApi : adminApi;
  const confirm = useConfirm();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const snaps = useQuery({ queryKey: ["draft-snapshots", base, reportId], queryFn: () => api.listDraftSnapshots(reportId) });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["draft-snapshots", base, reportId] });
    qc.invalidateQueries({ queryKey: ["draft", base, reportId] });
  };
  const save = useMutation({
    mutationFn: (label?: string) => api.saveDraftSnapshot(reportId, label),
    onSuccess: () => {
      toast.success("Draft version saved");
      refresh();
    },
    onError: (e) => toast.error("Couldn't save the draft version", e instanceof Error ? e.message : undefined),
  });
  const restore = useMutation({
    mutationFn: (s: DraftSnapshot) => api.restoreDraftSnapshot(reportId, s.id),
    onSuccess: () => {
      toast.success("Draft restored — opening the designer");
      refresh();
      navigate(`${base}/reports/${reportId}/edit`);
    },
    onError: (e) => toast.error("Restore failed", e instanceof Error ? e.message : undefined),
  });
  return (
    <Card
      flush
      title="Draft history"
      subtitle="Your working draft is checkpointed as you edit (the draft is never shown on screens until you publish). Restore an earlier draft to keep editing from it — the draft it replaces is kept here too."
      actions={
        <Button
          size="sm"
          icon={Save}
          loading={save.isPending}
          onClick={() => {
            const label = window.prompt("Name this draft version (optional)", "");
            if (label !== null) save.mutate(label.trim() || undefined);
          }}
        >
          Save draft version
        </Button>
      }
    >
      <div style={{ height: 12 }} />
      <DataTable<DraftSnapshot>
        rows={snaps.data ?? []}
        loading={snaps.isLoading}
        rowKey={(s) => s.id}
        empty={<EmptyState icon={History} title="No earlier drafts yet" description="Checkpoints appear here once you edit the draft." />}
        columns={[
          { key: "when", header: "Draft as of", cell: (s) => <span title={formatDateTime(s.captured_at)}>{formatRelative(s.captured_at)}</span> },
          { key: "kind", header: "Type", cell: (s) => <Badge tone={(SNAPSHOT_KIND[s.kind] ?? SNAPSHOT_KIND.auto).tone}>{(SNAPSHOT_KIND[s.kind] ?? SNAPSHOT_KIND.auto).label}</Badge> },
          { key: "label", header: "Name", cell: (s) => <span>{s.label ?? <span className="ds-muted">—</span>}</span> },
          { key: "size", header: "Contents", cell: (s) => <span className="ds-muted">{s.page_count} page{s.page_count === 1 ? "" : "s"} · {s.widget_count} widgets</span> },
          { key: "by", header: "By", cell: (s) => <span className="ds-muted">{(s.created_by && nameOf.get(s.created_by)) ?? "—"}</span> },
          {
            key: "act",
            header: "",
            align: "right",
            cell: (s) => (
              <Button
                size="sm"
                icon={RotateCcw}
                onClick={async () => {
                  if (await confirm({ title: "Restore this draft?", message: "It becomes your working draft and opens in the designer. The draft you have now is kept in this list. Screens are not affected until you publish.", confirmLabel: "Restore & edit" })) restore.mutate(s);
                }}
              >
                Restore &amp; edit
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );
}

function AdvancedTab({ reportId }: { reportId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const draft = useQuery({ queryKey: ["draft", "/admin", reportId], queryFn: () => adminApi.getDraft(reportId) });
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (draft.data) setText(JSON.stringify(draft.data.definition_json, null, 2));
  }, [draft.data]);
  const save = useMutation({
    mutationFn: async () => {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        throw new Error(`That isn't valid JSON: ${(e as Error).message}`);
      }
      return adminApi.saveDraft(reportId, { definition: parsed, expectedRevision: draft.data?.revision_number ?? 0 });
    },
    onSuccess: () => {
      setErr(null);
      toast.success("Draft saved");
      qc.invalidateQueries({ queryKey: ["draft"] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Save failed"),
  });
  return (
    <Card title="Report definition (JSON)" subtitle="For experts and AI tools. Most changes are easier in the designer." actions={<Badge tone="warning"><Info size={12} /> Edits the draft only</Badge>}>
      {draft.isLoading ? <SkeletonRows /> : (
        <div className="ds-stack">
          <Field error={err ?? undefined}>
            <Textarea code value={text} onChange={(e) => setText(e.target.value)} style={{ minHeight: 480 }} spellCheck={false} />
          </Field>
          <div className="ds-row" style={{ justifyContent: "flex-end" }}>
            <Button icon={Eye} onClick={() => window.open(`/tv-preview/preview.html?reportId=${reportId}`, "_blank", "noopener")}>Preview saved draft</Button>
            <Button variant="primary" icon={Save} loading={save.isPending} onClick={() => save.mutate()}>Save draft</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/** "3 screens (2 TVs, 1 laptop)" / "1 laptop" / "2 office TVs". */
function screenBreakdown(total: number, laptops: number): string {
  const tvs = total - laptops;
  const n = (k: number, one: string) => `${k} ${one}${k === 1 ? "" : "s"}`;
  if (!laptops) return n(tvs, "office TV");
  if (!tvs) return n(laptops, "laptop");
  return `${n(total, "screen")} (${n(tvs, "TV")}, ${n(laptops, "laptop")})`;
}
